import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	CreateTaskBody,
	ListTaskResponsiblesResponse,
	ListTasksParams,
	ListTasksResponse,
	Task,
	UpdateTaskBody,
} from './types/task'

export const tasksApi = createApi({
	reducerPath: 'tasksApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['Task', 'TaskResponsible'],
	endpoints: (builder) => ({
		listTasks: builder.query<ListTasksResponse, ListTasksParams | void>({
			query: (params) => ({ url: '/tasks', params: params ?? {} }),
			providesTags: (result) =>
				result
					? [
							...result.items.map((it) => ({ type: 'Task' as const, id: it.id })),
							{ type: 'Task' as const, id: 'LIST' },
						]
					: [{ type: 'Task' as const, id: 'LIST' }],
		}),

		getTask: builder.query<Task | null, string>({
			query: (id) => `/tasks/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'Task', id }],
		}),

		createTask: builder.mutation<Task | null, CreateTaskBody>({
			query: (body) => ({ url: '/tasks', method: 'POST', body }),
			invalidatesTags: [{ type: 'Task', id: 'LIST' }],
		}),

		updateTask: builder.mutation<Task | null, { id: string; body: UpdateTaskBody }>({
			query: ({ id, body }) => ({ url: `/tasks/${id}`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Task', id },
				{ type: 'Task', id: 'LIST' },
			],
		}),

		deleteTask: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/tasks/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'Task', id },
				{ type: 'Task', id: 'LIST' },
			],
		}),

		listTaskResponsibles: builder.query<ListTaskResponsiblesResponse, void>({
			query: () => '/tasks/responsibles',
			providesTags: [{ type: 'TaskResponsible', id: 'LIST' }],
		}),
	}),
})

export const {
	useListTasksQuery,
	useGetTaskQuery,
	useCreateTaskMutation,
	useUpdateTaskMutation,
	useDeleteTaskMutation,
	useListTaskResponsiblesQuery,
} = tasksApi
